import { c as _c } from "react/compiler-runtime";
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { stack } from '@/uiPrimitives/layout/stack';
import { gridStack } from '@/uiPrimitives/layout/gridStack';
import { box } from '@/uiPrimitives/layout/box';
import { textBlock, textInline } from '@/uiPrimitives/typography/text';
import { addTestSelector } from '@/globals/testHelpers/testSelector';
import { DOPLink } from '@/uiComposites/interactive/DOPLink';
import { H2TextBlock } from '@/uiComposites/typography/headings';
import { WebAPIAmendmentsPage } from '@/globals/webApiTypes/pages/WebAPIAmendmentsPage';
import { translateInline } from '@/globals/translate/translateInline';
import { pluralize } from '@/globals/helpers/pluralize';
import { EffectiveDateText } from './EffectiveDate';
import React from 'react';
import { AppPromoCard } from '@/sections/card/appPromoCard/AppPromoCard';
import { groupGhost, groupHeader, resultsGrid } from './Amendments.css';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { hiddenAccessibleBelowMQ640px } from '@/uiPrimitives/utility/display.css';
const isDateInFuture = (dateString: string | undefined): boolean => {
  // A missing date means the effective date is not yet known, so treat it as future.
  if (!dateString) return true;
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date > today;
};
const getGroupedResults = (results: WebAPIAmendmentsPage['results'] | undefined) => {
  if (!results) return [];
  const futureGroup = {
    key: 'yet-to-come-into-effect',
    label: translateInline({
      nl: 'Moet nog ingaan',
      en: 'Yet to come into effect'
    }),
    results: [] as WebAPIAmendmentsPage['results']
  };
  const pastGroup = {
    key: 'already-in-effect',
    label: translateInline({
      nl: 'Al ingegaan',
      en: 'Already in effect'
    }),
    results: [] as WebAPIAmendmentsPage['results']
  };

  // Group results by whether date has passed
  results.forEach(result => {
    if (isDateInFuture(result.effectiveDateInformation?.date)) {
      futureGroup.results.push(result);
    } else {
      pastGroup.results.push(result);
    }
  });
  const getEffectiveTime = (dateString: string | undefined) => dateString ? new Date(dateString).getTime() : Number.MAX_SAFE_INTEGER;

  // Future: soonest first. Past: most recent first. No exact date last.
  const sortByEffectiveDate = (list: WebAPIAmendmentsPage['results'], direction: 1 | -1) => list.sort((a, b) => direction * (getEffectiveTime(a.effectiveDateInformation?.date) - getEffectiveTime(b.effectiveDateInformation?.date)));

  // Return only non-empty groups, with future dates first
  const grouped = [];
  sortByEffectiveDate(futureGroup.results, 1);
  if (futureGroup.results.length > 0) {
    grouped.push(futureGroup);
  }
  sortByEffectiveDate(pastGroup.results, -1);
  if (pastGroup.results.length > 0) {
    grouped.push(pastGroup);
  }
  return grouped;
};
export const AmendmentsResults = t0 => {
  const $ = _c(30);
  const {
    results,
    promos
  } = t0;
  const t1 = results?.length ?? 0;
  let t2;
  if ($[0] !== t1) {
    t2 = pluralize(t1, {
      none: translateInline({
        en: "No amendments found",
        nl: "Geen wetswijzigingen gevonden"
      }),
      single: translateInline({
        en: "amendment",
        nl: "wetswijziging"
      }),
      plural: translateInline({
        en: "amendments",
        nl: "wetswijzigingen"
      })
    });
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const resultCountText = t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[2] !== resultCountText || $[3] !== results) {
    const groupedResults = getGroupedResults(results);
    let t8;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = classes(stack({
        gap: "7 | betweenH3Sections"
      }));
      t6 = addTestSelector("uiResultList");
      t8 = classes(textBlock({
        capSize: "-1 | small",
        color: "positive"
      }));
      $[9] = t5;
      $[10] = t6;
      $[11] = t8;
    } else {
      t5 = $[9];
      t6 = $[10];
      t8 = $[11];
    }
    let t9;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = addTestSelector("uiFilterResultCount");
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    let t10;
    if ($[13] !== results?.length) {
      t10 = (results?.length ?? 0) > 0 && <><span className={classes(textInline({
          fontWeight: "bold"
        }))}>{results?.length ?? 0}</span>{" "}</>;
      $[13] = results?.length;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    if ($[15] !== resultCountText || $[16] !== t10) {
      t7 = <div className={t8} role="status" {...t9}>{t10}{resultCountText}</div>;
      $[15] = resultCountText;
      $[16] = t10;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = classes(resultsGrid);
      $[18] = t3;
    } else {
      t3 = $[18];
    }
    t4 = groupedResults.map(_temp2);
    $[2] = resultCountText;
    $[3] = results;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
  }
  let t8;
  if ($[19] !== t3 || $[20] !== t4) {
    t8 = <div className={t3}>{t4}</div>;
    $[19] = t3;
    $[20] = t4;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== promos.nieuweWettenApp) {
    t9 = promos.nieuweWettenApp != null && <AppPromoCard {...promos.nieuweWettenApp} />;
    $[22] = promos.nieuweWettenApp;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t5 || $[25] !== t6 || $[26] !== t7 || $[27] !== t8 || $[28] !== t9) {
    t10 = <div className={t5} {...t6}>{t7}{t8}{t9}</div>;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
};
function _temp(t0, resultIndex) {
  const {
    href,
    title,
    text,
    effectiveDateInformation
  } = t0;
  return <div key={resultIndex} className={classes(stack({
    gap: "2 | h2"
  }))}><DOPLink data-link-type="amendmentsResult" href={href} className={classes(stack({
      gap: "2 | h2"
    }))}><H2TextBlock size="h4" textBlockProps={{
        color: "positiveBlue",
        textDecorationLine: "underline"
      }}>{title}</H2TextBlock>{effectiveDateInformation != null && <EffectiveDateText effectiveDateInformation={effectiveDateInformation} />}<p className={classes(textBlock({
        textDecorationLine: "none",
        fontVariantNumeric: "oldstyle-nums"
      }))}>{text}</p></DOPLink></div>;
}
function _temp2(group, groupIndex) {
  const groupResultCountText = pluralize(group.results.length, {
    none: translateInline({
      en: "amendments",
      nl: "wetswijzigingen"
    }),
    single: translateInline({
      en: "amendment",
      nl: "wetswijziging"
    }),
    plural: translateInline({
      en: "amendments",
      nl: "wetswijzigingen"
    })
  });
  return <React.Fragment key={group.key}><span id={`group-results-${groupIndex}`} tabIndex={-1} className={groupGhost} /><button onClick={() => {
      const element = document.getElementById(`group-results-${groupIndex}`);
      element?.focus({
        preventScroll: true
      });
      element?.scrollIntoView({
        behavior: "smooth"
      });
    }} className={classes(box({
      backgroundColor: "negativeBlue",
      paddingBlock: "2 | h2",
      paddingInline: "2 | h2"
    }), groupHeader)}><div className={classes(shelf({
        gap: "0 | p",
        alignItems: "baseline"
      }))}><h2 className={classes(textBlock({
          capSize: "1 | h3"
        }))}>{group.label}</h2><span className={classes(textBlock({
          capSize: "0 | p",
          fontStyle: "italic",
          color: "positiveMuted"
        }))}>{`(${group.results.length}`}<span className={hiddenAccessibleBelowMQ640px}>{` ${groupResultCountText}`}</span>{")"}</span></div></button><div className={classes(gridStack({
      gap: "7 | betweenH3Sections"
    }))}>{group.results.map(_temp)}</div></React.Fragment>;
}